import React, { useEffect, useRef } from 'react';
import { FocusOn } from 'react-focus-on';
import classnames from 'classnames';
import { useOutsideClick } from '../../hooks/use-outside-click';

import styles from './dialog-customizable.module.scss';

export type DialogCustomizableProps = {
  isOpen: boolean;
  children: React.ReactNode;
  outsideClick: () => void;
  className?: string;
};

const noScrollClass = 'no-scroll';

export const DialogCustomizable: React.FC<DialogCustomizableProps> = ({
  isOpen,
  outsideClick,
  children,
  className
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const classes = classnames(styles.dialogCustom, { [styles.open]: isOpen }, className);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add(noScrollClass);

      if (!ref.current || !scrollContainerRef.current) {
        return;
      }

      scrollContainerRef.current.scrollTop = 0;
    }

    return () => {
      document.body.classList.remove(noScrollClass);
    };
  }, [isOpen]);

  useOutsideClick(ref, outsideClick);

  return (
    <>
      <FocusOn enabled={isOpen} onEscapeKey={outsideClick} autoFocus={false}>
        <div aria-hidden={!isOpen} className={classes} data-testid="dialog-component">
          <div className={styles.inner} ref={ref}>
            <div className={styles.scrollContainer} ref={scrollContainerRef}>
              {children}
            </div>
          </div>
        </div>
      </FocusOn>
      {isOpen && <div className={styles.dialogCustomLayer} />}
    </>
  );
};
